/**
 * Pure rules for the Individual Bot Screen (DASHBOARD.md §1.14): live KPIs, the
 * recommendations table (filter, sort, CSV), the job description sections and
 * the documents table. No React and no fetching.
 *
 * A recommendation's decision and implementation status are the LIVE ones from
 * the snapshot overlay when it is still an Action Center item (it has an
 * action_id the overlay knows), otherwise what the snapshot recorded. Zero
 * denominators give null ("—"), never 0%.
 */

import { ActionItem, Bot, BotDocument, DecisionValue, ImplStatus, Recommendation } from './types';

export interface RecRow {
  rec: Recommendation;
  decision: DecisionValue;
  decidedAt: string | null;
  impl: ImplStatus;
  /** The live Action Center item, when this recommendation still has one. */
  action: ActionItem | null;
  verdict: 'agree' | 'alternative' | 'none';
}

export function recRows(bot: Bot, detail: { recommendations?: Recommendation[] }): RecRow[] {
  const live = new Map<string, ActionItem>((bot.actions || []).map((a) => [a.id, a]));
  return (detail.recommendations || []).map((rec) => {
    const action = (rec.action_id && live.get(rec.action_id)) || null;
    return {
      rec,
      action,
      decision: (action ? action.decision && action.decision.value : rec.decision && rec.decision.value) || 'pending',
      decidedAt: (action ? action.decision && action.decision.decided_at : rec.decision && rec.decision.decided_at) || null,
      impl: (action ? action.implementation && action.implementation.status : rec.implementation && rec.implementation.status) || 'not_started',
      verdict: (rec.verdict && rec.verdict.status) || 'none',
    };
  });
}

const pct = (n: number, d: number): number | null => (d > 0 ? Math.round((n / d) * 1000) / 10 : null);

export interface BotKpis {
  total: number;
  reviewed: number;
  unreviewed: number;
  agreed: number;
  alternative: number;
  agreed_pct: number | null;
  approved: number;
  decided: number;
  approved_pct: number | null;
  done: number;
  implemented_pct: number | null;
  implemented_total_pct: number | null;
}

export function botKpis(rows: RecRow[]): BotKpis {
  const total = rows.length;
  const agreed = rows.filter((r) => r.verdict === 'agree').length;
  const alternative = rows.filter((r) => r.verdict === 'alternative').length;
  const reviewed = agreed + alternative;
  const approved = rows.filter((r) => r.decision === 'yes').length;
  const decided = rows.filter((r) => r.decision !== 'pending').length;
  const done = rows.filter((r) => r.decision === 'yes' && r.impl === 'done').length;
  return {
    total, reviewed, unreviewed: total - reviewed, agreed, alternative,
    agreed_pct: pct(agreed, reviewed),
    approved, decided, approved_pct: pct(approved, total),
    done, implemented_pct: pct(done, approved), implemented_total_pct: pct(done, total),
  };
}

export const KPI_FORMULAS = {
  total: 'Distinct recommendations across all dated files, grouped by normalised title. A re-issue counts as a new version of the same recommendation.',
  agreed_pct: 'C-suite agreed ÷ reviewed × 100. Unreviewed items are excluded and shown separately.',
  approved_pct: 'Grant Yes ÷ total recommendations × 100.',
  implemented_pct: 'Implementation done ÷ Grant-approved × 100. "—" when nothing is approved yet.',
  implemented_total_pct: 'Implementation done ÷ total recommendations × 100.',
};

// ---- Recommendations table -------------------------------------------------

export type RecSortKey = 'date' | 'priority' | 'finding' | 'verdict' | 'decision' | 'implementation' | 'source';
export const REC_SORT_KEYS: RecSortKey[] = ['date', 'priority', 'finding', 'verdict', 'decision', 'implementation', 'source'];

export interface RecFilters {
  q: string;
  verdict: 'all' | 'agree' | 'alternative' | 'none';
  decision: 'all' | DecisionValue;
  priority: 'all' | 'P0' | 'P1' | 'P2' | 'none';
  implementation: 'all' | ImplStatus;
}
export const NO_REC_FILTERS: RecFilters = { q: '', verdict: 'all', decision: 'all', priority: 'all', implementation: 'all' };

const lower = (v: unknown) => String(v == null ? '' : v).toLowerCase();

export function filterRecs(rows: RecRow[], f: RecFilters): RecRow[] {
  const q = f.q.trim().toLowerCase();
  return rows.filter((r) => {
    if (f.verdict !== 'all' && r.verdict !== f.verdict) return false;
    if (f.decision !== 'all' && r.decision !== f.decision) return false;
    if (f.implementation !== 'all' && r.impl !== f.implementation) return false;
    if (f.priority !== 'all' && (r.rec.priority_code || 'none') !== f.priority) return false;
    if (!q) return true;
    const rec = r.rec;
    return [rec.title, rec.finding, rec.recommended_action, rec.verdict && rec.verdict.text, rec.bucket, rec.source && rec.source.path, rec.id]
      .some((v) => lower(v).includes(q));
  });
}

const PRIORITY_RANK: Record<string, number> = { P0: 0, P1: 1, P2: 2 };
const VERDICT_RANK: Record<string, number> = { alternative: 0, agree: 1, none: 2 };
const DECISION_RANK: Record<string, number> = { pending: 0, instructed: 1, yes: 2, no: 3 };
const IMPL_RANK: Record<string, number> = { blocked: 0, failed: 1, in_progress: 2, queued: 3, not_started: 4, done: 5 };

export function sortRecs(rows: RecRow[], key: RecSortKey, dir: 'asc' | 'desc'): RecRow[] {
  const val = (r: RecRow): string | number => {
    switch (key) {
      case 'date': return r.rec.date || '';
      case 'priority': return (PRIORITY_RANK[r.rec.priority_code || ''] ?? 3) * 1000 - (typeof r.rec.score === 'number' ? r.rec.score : 0);
      case 'finding': return lower(r.rec.title);
      case 'verdict': return VERDICT_RANK[r.verdict] ?? 3;
      case 'decision': return DECISION_RANK[r.decision] ?? 4;
      case 'implementation': return IMPL_RANK[r.impl] ?? 6;
      case 'source': return lower(r.rec.source && r.rec.source.path);
      default: return 0;
    }
  };
  const sign = dir === 'asc' ? 1 : -1;
  return rows.slice().sort((a, b) => {
    const x = val(a); const y = val(b);
    const c = x < y ? -1 : x > y ? 1 : 0;
    if (c) return c * sign;
    // Same priority and score: the newest recommendation first.
    if (key === 'priority' && (a.rec.date || '') !== (b.rec.date || '')) return (a.rec.date || '') < (b.rec.date || '') ? 1 : -1;
    return a.rec.id < b.rec.id ? -1 : 1;
  });
}

/** One CSV cell: quoted, quotes doubled, and a leading = + - @ neutralised so a spreadsheet never runs it as a formula. */
export function csvCell(v: unknown): string {
  let s = v == null ? '' : String(v).replace(/\r?\n/g, ' ');
  if (/^[=+\-@\t]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}

export function recsToCsv(rows: RecRow[]): string {
  const head = ['Date', 'Priority', 'Score', 'Finding', 'Recommended action', 'Recommended by', 'C-suite verdict', 'Exec', 'Grant decision', 'Implementation', 'Source', 'Id'];
  const lines = rows.map((r) => [
    r.rec.date, r.rec.priority_code, r.rec.score, r.rec.title, r.rec.recommended_action, r.rec.recommended_by, r.verdict, r.rec.verdict && r.rec.verdict.exec,
    r.decision, r.impl, r.rec.source ? `${r.rec.source.path}${r.rec.source.line ? `:${r.rec.source.line}` : ''}` : '', r.rec.id,
  ].map(csvCell).join(','));
  return [head.map(csvCell).join(','), ...lines].join('\r\n');
}

// ---- Job description --------------------------------------------------------

export interface JdSection { heading: string; body: string; kind: string; defaultOpen: boolean }

/** One collapsible section per `##` heading; kind from the snapshot's jd_sections. Open by default unless it is "other". */
export function parseJd(markdown: string | null | undefined, kinds: { heading: string; kind: string }[] = []): { title: string | null; intro: string; sections: JdSection[] } {
  const text = (markdown || '').replace(/\r\n/g, '\n');
  if (!text.trim()) return { title: null, intro: '', sections: [] };
  const lines = text.split('\n');
  let title: string | null = null;
  const intro: string[] = [];
  const sections: JdSection[] = [];
  let cur: { heading: string; lines: string[] } | null = null;
  let fence = false;
  const kindOf = (h: string) => (kinds.find((k) => k.heading.trim().toLowerCase() === h.trim().toLowerCase()) || { kind: 'other' }).kind;
  const flush = () => {
    if (!cur) return;
    const kind = kindOf(cur.heading);
    sections.push({ heading: cur.heading, body: cur.lines.join('\n').trim(), kind, defaultOpen: kind !== 'other' });
    cur = null;
  };
  for (const line of lines) {
    if (/^```/.test(line.trim())) fence = !fence;
    const h2: RegExpExecArray | null = !fence ? /^##\s+(.+?)\s*#*\s*$/.exec(line) : null;
    const h1: RegExpExecArray | null = !fence && !title ? /^#\s+(.+?)\s*#*\s*$/.exec(line) : null;
    if (h2) { flush(); cur = { heading: h2[1], lines: [] }; } else if (h1) { title = h1[1]; } else if (cur) { cur.lines.push(line); } else { intro.push(line); }
  }
  flush();
  return { title, intro: intro.join('\n').trim(), sections };
}

// ---- Documents ----------------------------------------------------------------

export type DocSortKey = 'title' | 'type' | 'date' | 'modified' | 'size' | 'version' | 'scan';
export const DOC_SORT_KEYS: DocSortKey[] = ['title', 'type', 'date', 'modified', 'size', 'version', 'scan'];

export const DOWNLOADABLE_SCANS = ['clean', 'binary_unscanned'];
export const isDownloadable = (d: BotDocument): boolean => Boolean(d.s3_key) && DOWNLOADABLE_SCANS.includes(d.scan && d.scan.status);

export const SCAN_LABEL: Record<string, string> = {
  clean: 'Clean', binary_unscanned: 'Not scanned (binary)', flagged_pii: 'Withheld (personal data)', flagged_secret: 'Withheld (possible secret)',
  too_large: 'Withheld (over 25 MB)', unreadable: 'Withheld (unreadable)',
};

export function filterDocs(docs: BotDocument[], f: { q: string; type: string; scan: string }): BotDocument[] {
  const q = f.q.trim().toLowerCase();
  return docs.filter((d) => {
    if (f.type !== 'all' && d.type !== f.type) return false;
    if (f.scan === 'withheld' && isDownloadable(d)) return false;
    if (f.scan !== 'all' && f.scan !== 'withheld' && d.scan.status !== f.scan) return false;
    return !q || [d.title, d.filename, d.path, d.type].some((v) => lower(v).includes(q));
  });
}

export function sortDocs(docs: BotDocument[], key: DocSortKey, dir: 'asc' | 'desc'): BotDocument[] {
  const val = (d: BotDocument): string | number => {
    switch (key) {
      case 'title': return lower(d.title);
      case 'type': return lower(d.type);
      case 'date': return d.created_at || '';
      case 'modified': return d.modified_at || '';
      case 'size': return d.size_bytes ?? -1;
      case 'version': return d.version || '';
      case 'scan': return d.scan.status;
      default: return 0;
    }
  };
  const sign = dir === 'asc' ? 1 : -1;
  return docs.slice().sort((a, b) => {
    // Missing dates always sink to the bottom, whichever way the column is sorted.
    if ((key === 'date' || key === 'modified') && !val(a) !== !val(b)) return val(a) ? -1 : 1;
    const x = val(a); const y = val(b);
    const c = x < y ? -1 : x > y ? 1 : 0;
    return c ? c * sign : a.path < b.path ? -1 : 1;
  });
}

export const fmtBytes = (n?: number | null): string => {
  if (n == null) return 'no data';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

// ---- Heartbeat ----------------------------------------------------------------

/** 0 to 4: how dark a cell is, scaled to the busiest hour. */
export function heat(count: number | null | undefined, max: number): number {
  if (!count || max <= 0) return 0;
  return Math.max(1, Math.min(4, Math.ceil((count / max) * 4)));
}
